import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { isNotFoundError } from '@/lib/api/client'
import {
  useVendorEvent,
  useEventCategories,
  useLocationStats,
  useVendorEventCountStats,
  useMenuItems,
  useEvent,
  useUpdateVendorEvent,
  useDeleteVendorEvent,
  useVendorEventAdministrators,
  useCreateVendorEventAdministrator,
  useUpdateVendorEventAdministrator,
  useDeleteVendorEventAdministrator,
  usePermissionGroups,
  useItemReviews,
  invalidateLocationCaches,
  useCanAddMenuItems,
} from '@/lib/api/queries'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToServerNaive } from '@/lib/eventTime'
import { DateFilter } from '@/components/ui/date-filter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  ArrowLeft, 
  Pencil, 
  Users, 
  Package, 
  ShoppingCart, 
  Coins,
  Plus,
  Coffee,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  MapPin,
  Info,
  UserPlus,
  MoreVertical,
  Trash2,
  Shield,
  KeyRound,
  ChevronDown,
  Star,
  ThumbsUp,
  Check,
  CreditCard,
  Upload,
} from 'lucide-react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import type { MenuItem, VendorEvent, Vendor, VendorEventAdministrator, PermissionGroup } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { useState, useMemo, useEffect, useRef } from 'react'
import { CreateMenuItemModal, EditMenuItemModal } from './menu'
import { ActiveDiscountBadge } from '@/features/promotions/active-discount-badge'
import { hasDiscountedPrice } from '@/features/promotions/menu-price'
import { formatTokenNumber } from '@/features/promotions/money'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts'
import { format } from 'date-fns'
import { ExportLoginPdfModal } from '@/components/export-login-pdf-modal'

// Convert 24hr to 12hr format: "15:00" -> "3 PM", 15 -> "3 PM"
const formatHour12 = (hour: number | string): string => {
  const h = typeof hour === 'string' ? parseInt(hour.split(':')[0]) : hour
  if (h === 0) return '12 AM'
  if (h === 12) return '12 PM'
  if (h < 12) return `${h} AM`
  return `${h - 12} PM`
}

export const Route = createFileRoute('/_authenticated/events/$eventId/vendors/$vendorId/locations/$locationId')({
  component: LocationDetailPage,
})

function LocationDetailPage() {
  const { eventId, vendorId, locationId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const locationIdNum = Number(locationId)
  
  // Permission checks
  const canViewVendors = usePermission('can_view_vendors')
  const canEditLocations = usePermission('can_edit_locations')
  const canCreateMenu = useCanAddMenuItems(eventIdNum)
  const canEditMenu = usePermission('can_edit_menu')
  const canExportVendorLogins = usePermission('can_export_vendor_logins')
  
  // Modal state
  const [editingItemId, setEditingItemId] = useState<number | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditLocationModal, setShowEditLocationModal] = useState(false)
  const [showExportLoginPdfModal, setShowExportLoginPdfModal] = useState(false)
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false)
  const optionsDropdownRef = useRef<HTMLDivElement>(null)
  
  // Tab state with URL hash
  type LocationTab = 'overview' | 'menu' | 'reporting' | 'staff'
  const getInitialTab = (): LocationTab => {
    const hash = window.location.hash.replace('#', '')
    if (hash === 'menu' || hash === 'reporting' || hash === 'staff') return hash
    return 'overview'
  }
  const [activeTab, setActiveTab] = useState<LocationTab>(getInitialTab)
  
  // Sync tab with URL hash
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') as LocationTab
      if (hash === 'menu' || hash === 'reporting' || hash === 'overview' || hash === 'staff') {
        setActiveTab(hash)
      }
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsDropdownRef.current && !optionsDropdownRef.current.contains(event.target as Node)) {
        setShowOptionsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleTabChange = (tab: LocationTab) => {
    setActiveTab(tab)
    window.history.pushState(null, '', `#${tab}`)
  }
  
  // Item reporting sort state
  type SortField = 'item_name' | 'order_count' | 'quantity_sold' | 'token_revenue' | 'cash_revenue'
  const [sortField, setSortField] = useState<SortField>('quantity_sold')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Item reporting date window (datetime-local, event-local time)
  const [reportFrom, setReportFrom] = useState('')
  const [reportTo, setReportTo] = useState('')

  // Permission guard
  if (!canViewVendors) {
    return <AccessDenied message="You don't have permission to view this location." />
  }
  
  // Fetch event details for tokenValue and eventType
  const { data: event } = useEvent(eventIdNum)
  
  // Fetch location details with expanded vendor and menu_items
  const { data: location, isLoading: locationLoading } = useVendorEvent(locationIdNum, {
    expand: 'vendor,menu_items',
    aggregates: true,
    event: eventIdNum,
  })
  
  // Fetch location stats with all_items for item reporting
  const { data: stats, isLoading: statsLoading } = useLocationStats(locationIdNum, { all_items: true })

  // Server-naive date params for the item-reporting window; undefined when
  // no filter is set so the unfiltered stats.all_items is used.
  const reportDateParams = useMemo(() => {
    if (!reportFrom && !reportTo) return undefined
    const tz = event?.timezone || DEFAULT_EVENT_TIMEZONE
    const params: { created_at__gte?: string; created_at__lte?: string } = {}
    if (reportFrom) params.created_at__gte = eventLocalToServerNaive(reportFrom, tz)
    if (reportTo) params.created_at__lte = eventLocalToServerNaive(reportTo, tz)
    return params
  }, [reportFrom, reportTo, event?.timezone])

  // Date-scoped stats fetched only while a window is set, so Overview
  // numbers (fed by the unfiltered `stats`) never change with the filter.
  const { data: datedStats, isFetching: reportFiltering } = useLocationStats(
    locationIdNum,
    { all_items: true, ...reportDateParams },
    { enabled: !!reportDateParams },
  )

  // Fetch item reviews for this location to show ratings in item reporting table
  const { data: itemReviewsData } = useItemReviews({
    vendor_event: locationIdNum,
    expand: 'vendor_event_item',
    page_size: 200,
  })

  // Build a map of vendor_event_item_id -> aggregated review data
  const itemRatingsMap = useMemo(() => {
    const map = new Map<number, { totalRatings: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number }>()
    if (!itemReviewsData?.results) return map
    for (const review of itemReviewsData.results) {
      const itemId = typeof review.vendor_event_item === 'object' ? review.vendor_event_item.id : review.vendor_event_item
      if (!map.has(itemId)) {
        map.set(itemId, { totalRatings: 0, scaleSum: 0, scaleCount: 0, thumbsUp: 0, thumbsDown: 0 })
      }
      const entry = map.get(itemId)!
      entry.totalRatings++
      for (const cr of review.criterion_ratings || []) {
        if (cr.scale_rating != null) {
          entry.scaleSum += cr.scale_rating
          entry.scaleCount++
        }
        if (cr.thumbs_up != null) {
          if (cr.thumbs_up) entry.thumbsUp++
          else entry.thumbsDown++
        }
      }
    }
    return map
  }, [itemReviewsData?.results])
  
  // Fetch new location count stats with top items and hourly data
  const { data: countStats } = useVendorEventCountStats(locationIdNum, { top_count: 10, event: eventIdNum })
  
  // Fetch menu items for this location
  const { data: menuItemsData, isLoading: menuLoading } = useMenuItems({
    vendor_event: locationIdNum,
  })
  
  // Fetch location staff (vendor event administrators) for count
  const { data: locationStaffData } = useVendorEventAdministrators(locationIdNum, eventIdNum)
  
  const menuItems = menuItemsData?.results || []
  const locationStaff = locationStaffData?.results || []
  const tokenValue = event?.token_value || 2
  const eventType = event?.event_type || 'token'
  const isLoading = locationLoading || statsLoading

  // Stats helpers - use new countStats when available, fallback to legacy stats
  const getCustomerCount = () => stats?.customer_count || stats?.customers || 0
  const getTotalOrders = () => countStats?.totals?.total_orders || stats?.total_orders || 0
  const getItemsSold = () => countStats?.totals?.total_items || stats?.items || 0
  const getRevenue = () => countStats?.totals?.total_tokens || stats?.tokens_spent || 0
  const getCashRevenue = () => countStats?.totals?.total_amount || 0

  // Chart data from time_data (legacy endpoint)
  const chartData = useMemo(() => {
    const timeData = stats?.time_data || []
    return timeData.map(entry => {
      let formattedDate = entry.date
      try {
        // Parse date format from '2024-08-17 20' to 'Aug 17 20:00'
        const dateObj = new Date(entry.date.replace(' ', 'T') + ':00:00')
        formattedDate = format(dateObj, 'MMM dd HH:00')
      } catch {
        // Keep original if parsing fails
      }
      return {
        date: formattedDate,
        amount: entry.amount || 0,
      }
    })
  }, [stats?.time_data])

  // Hourly sales data from new endpoint with 12hr format
  const hourlyData = useMemo(() => {
    return (countStats?.sales_by_hour || []).map(entry => ({
      ...entry,
      hour_label_12: formatHour12(entry.hour)
    }))
  }, [countStats?.sales_by_hour])

  // Top items data from new endpoint
  const topItems = useMemo(() => {
    return countStats?.top_items || []
  }, [countStats?.top_items])

  // Sorted item reporting data — date-scoped rows when a window is set
  // (falling back to the unfiltered rows while the scoped fetch loads,
  // dimmed via reportFiltering).
  const sortedItems = useMemo(() => {
    const items = (reportDateParams ? datedStats?.all_items ?? stats?.all_items : stats?.all_items) || []
    return [...items].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal)
      }
      return sortDirection === 'asc' 
        ? (aVal as number) - (bVal as number) 
        : (bVal as number) - (aVal as number)
    })
  }, [stats?.all_items, datedStats?.all_items, reportDateParams, sortField, sortDirection])

  // Toggle sort function
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Sort icon helper
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />
  }

  if (isLoading) {
    return (
      <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-6 w-32 bg-neutral-200 dark:bg-neutral-800 rounded" />
          <div className="h-10 w-64 bg-neutral-200 dark:bg-neutral-800 rounded" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-neutral-200 dark:bg-neutral-800 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!location) {
    return (
      <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
        <p className="text-neutral-500">Location not found</p>
      </div>
    )
  }

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-8">
      {/* Back Button and Edit (mobile) */}
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/events/$eventId/vendors/$vendorId"
          params={{ eventId, vendorId }}
          className="flex items-center text-sm text-blue-600 dark:text-blue-500 hover:text-blue-800 dark:hover:text-blue-400"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Vendor
        </Link>
      </div>

      {/* Location Title and Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {location.booth || location.name || 'Location'}
          </h1>
          <span className={cn(
            'mt-2 sm:mt-0 sm:ml-3 px-2 py-1 text-xs rounded-full inline-flex w-fit',
            location.is_approved
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
          )}>
            {location.is_approved ? 'Approved' : 'Not Approved'}
          </span>
        </div>
        {(canEditLocations || canExportVendorLogins) && (
          <div className="relative" ref={optionsDropdownRef}>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
            >
              Options
              <ChevronDown className={cn('w-4 h-4 transition-transform', showOptionsDropdown && 'rotate-180')} />
            </Button>
            {showOptionsDropdown && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-800 py-1 min-w-[200px]">
                {canExportVendorLogins && (
                  <button
                    onClick={() => { setShowExportLoginPdfModal(true); setShowOptionsDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <KeyRound className="w-4 h-4 text-blue-600 dark:text-blue-500" />
                    Export Login PDF
                  </button>
                )}
                {canEditLocations && (
                  <button
                    onClick={() => { setShowEditLocationModal(true); setShowOptionsDropdown(false) }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <Pencil className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
                    Edit Location
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex space-x-4 sm:space-x-6">
          <button
            onClick={() => handleTabChange('overview')}
            className={cn(
              'px-1 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('menu')}
            className={cn(
              'px-1 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'menu'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            Menu Items ({menuItems.length})
          </button>
          <button
            onClick={() => handleTabChange('staff')}
            className={cn(
              'px-1 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'staff'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            Staff ({locationStaff.length})
          </button>
          <button
            onClick={() => handleTabChange('reporting')}
            className={cn(
              'px-1 py-3 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition-colors',
              activeTab === 'reporting'
                ? 'border-blue-600 text-blue-600 dark:text-blue-500'
                : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
            )}
          >
            Item Reporting
          </button>
        </div>
      </div>

      {/* Overview Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Customers Card */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
              <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900 mr-3">
                <Users className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">CUSTOMERS</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {getCustomerCount().toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Orders Card */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
              <div className="p-2 rounded-full bg-pink-100 dark:bg-pink-900/20 mr-3">
                <Package className="h-5 w-5 text-pink-500 dark:text-pink-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">ORDERS</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {getTotalOrders().toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Items Card */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/20 mr-3">
                <ShoppingCart className="h-5 w-5 text-amber-500 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">ITEMS</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {getItemsSold().toLocaleString()}
                </p>
              </div>
            </div>
            
            {/* Revenue Card */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 flex items-start">
              <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/20 mr-3">
                <Coins className="h-5 w-5 text-green-500 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">REVENUE</p>
                <p className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                  {getRevenue().toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Revenue Chart */}
          <div className="w-full bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <div className="px-6 pt-6">
              <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{location.booth || location.name} Revenue</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Hourly revenue trends in tokens</p>
            </div>
            <div className="px-4 py-6">
              {chartData.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="date" 
                        angle={-45} 
                        textAnchor="end" 
                        height={70}
                        tick={{ fontSize: 10, fontWeight: 500 }}
                        stroke="#9ca3af"
                        tickMargin={8}
                      />
                      <YAxis 
                        stroke="#9ca3af" 
                        tick={{ fontSize: 10, fontWeight: 600 }}
                        tickFormatter={(value) => value.toLocaleString()}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={false}
                        name="Tokens"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[400px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                  <p className="text-neutral-400">No revenue data available</p>
                </div>
              )}
            </div>
          </div>

          {/* New Charts Row - Top Items and Sales by Hour */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            {/* Top Selling Items */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
              <div className="px-6 pt-6 pb-2">
                <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Top Selling Items</h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Best performers by quantity sold</p>
              </div>
              <div className="px-6 pb-6">
                {topItems.length > 0 ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={topItems.slice(0, 8)} 
                        layout="vertical" 
                        margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
                        <XAxis 
                          type="number" 
                          stroke="#9ca3af" 
                          tick={{ fontSize: 11, fontWeight: 500 }}
                          tickFormatter={(value) => value.toLocaleString()}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="item_name" 
                          stroke="#9ca3af" 
                          tick={{ fontSize: 11, fontWeight: 500 }}
                          width={110}
                          tickMargin={8}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => {
                            if (name === 'quantity_sold') return [(value ?? 0).toLocaleString(), 'Qty Sold']
                            return [(value ?? 0).toLocaleString(), String(name)]
                          }}
                        />
                        <Bar dataKey="quantity_sold" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                          {topItems.slice(0, 8).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={index === 0 ? '#22c55e' : '#3b82f6'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[320px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                    <p className="text-neutral-400">No item data available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sales by Hour */}
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
              <div className="px-6 pt-6 pb-2 flex justify-between items-start">
                <div>
                  <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Sales by Hour</h2>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Order distribution throughout the day</p>
                </div>
                {countStats?.top_hour && (
                  <div className="text-right">
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">Peak Hour</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">{formatHour12(countStats.top_hour.hour)}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{countStats.top_hour.order_count.toLocaleString()} orders</p>
                  </div>
                )}
              </div>
              <div className="px-6 pb-6">
                {hourlyData.length > 0 ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis 
                          dataKey="hour_label_12" 
                          stroke="#9ca3af" 
                          tick={{ fontSize: 10, fontWeight: 500 }}
                          angle={-45}
                          textAnchor="end"
                          height={55}
                          tickMargin={5}
                        />
                        <YAxis 
                          stroke="#9ca3af" 
                          tick={{ fontSize: 11, fontWeight: 500 }}
                          tickFormatter={(value) => value.toLocaleString()}
                          tickMargin={8}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => {
                            if (name === 'order_count') return [(value ?? 0).toLocaleString(), 'Orders']
                            if (name === 'items_sold') return [(value ?? 0).toLocaleString(), 'Items']
                            if (name === 'token_revenue') return [(value ?? 0).toLocaleString(), 'Tokens']
                            return [(value ?? 0).toLocaleString(), String(name)]
                          }}
                        />
                        <Bar dataKey="order_count" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                          {hourlyData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={countStats?.top_hour && entry.hour === countStats.top_hour.hour ? '#22c55e' : '#8b5cf6'} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[320px] bg-neutral-50 dark:bg-neutral-900 rounded flex items-center justify-center">
                    <p className="text-neutral-400">No hourly data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats Row */}
          {countStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              {/* Best Selling Item */}
              {topItems[0] && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-green-600 dark:text-green-400">🔥</span>
                    <p className="text-xs font-medium text-green-700 dark:text-green-300 uppercase">Best Seller</p>
                  </div>
                  <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">{topItems[0].item_name}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{topItems[0].quantity_sold.toLocaleString()} sold</p>
                </div>
              )}

              {/* Peak Hour */}
              {countStats.top_hour && (
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-purple-600 dark:text-purple-400">⏰</span>
                    <p className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase">Peak Hour</p>
                  </div>
                  <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{formatHour12(countStats.top_hour.hour)}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">{countStats.top_hour.order_count.toLocaleString()} orders</p>
                </div>
              )}

              {/* Cash Revenue */}
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-amber-600 dark:text-amber-400">💰</span>
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase">Cash Revenue</p>
                </div>
                <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">${getCashRevenue().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Total cash sales</p>
              </div>

              {/* Items per Order */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-blue-600 dark:text-blue-400">📦</span>
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase">Items/Order</p>
                </div>
                <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">
                  {getTotalOrders() > 0 ? (getItemsSold() / getTotalOrders()).toFixed(1) : '0'}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Avg items per order</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Menu Items Tab Content */}
      {activeTab === 'menu' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Menu Items</h2>
            {canCreateMenu && (
              <Button className="gap-2" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                New Item
              </Button>
            )}
          </div>
        
        {menuLoading ? (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 text-center">
            <div className="animate-pulse">Loading menu items...</div>
          </div>
        ) : menuItems.length === 0 ? (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 text-center">
            <Coffee className="h-12 w-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No Menu Items</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">This location doesn't have any menu items yet.</p>
            {canCreateMenu && (
              <Button className="mt-4 gap-2" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4" />
                Create First Item
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Item Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Dietary
                    </th>
                    {canEditMenu && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {menuItems.map((item: MenuItem) => (
                    <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {item.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 dark:text-neutral-400">
                        {item.category ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200">
                            {item.category}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100">
                        {hasDiscountedPrice(item) ? (
                          <ActiveDiscountBadge item={item} formatPrice={(price) => `${formatTokenNumber(price, false)} Tokens`} />
                        ) : (
                          <span className="font-medium">{item.price} Tokens</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-500 dark:text-neutral-400 max-w-xs">
                        <div className="truncate">
                          {item.description || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          {item.is_available ? (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" title="Available">
                              <Check className="h-3 w-3" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                              Unavailable
                            </span>
                          )}
                          {item.direct_pay && (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" title="Direct Pay">
                              <CreditCard className="h-3 w-3" />
                            </span>
                          )}
                          {item.direct_pay_only_pos && (
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400" title="Direct pay from POS only">
                              <CreditCard className="h-3 w-3" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {item.is_vegetarian && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400" title="Vegetarian">V</span>
                          )}
                          {item.is_vegan && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400" title="Vegan">VG</span>
                          )}
                          {(item.is_gluten_free || item.is_glutenfree) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400" title="Gluten Free">GF</span>
                          )}
                          {(item.is_dairy_free || item.is_dairyfree) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400" title="Dairy Free">DF</span>
                          )}
                        </div>
                      </td>
                      {canEditMenu && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button 
                            onClick={() => setEditingItemId(item.id)}
                            className="text-neutral-400 hover:text-blue-600 dark:text-neutral-500 dark:hover:text-blue-400 transition-colors duration-200"
                            title="Edit item"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Staff Tab Content */}
      {activeTab === 'staff' && (
        <LocationUsersTab locationId={locationIdNum} eventId={eventIdNum} />
      )}

      {/* Item Reporting Tab Content */}
      {activeTab === 'reporting' && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Item Reporting</h2>
            <DateFilter
              value={{ gte: reportFrom || undefined, lte: reportTo || undefined }}
              onChange={(v) => { setReportFrom(v.gte ?? ''); setReportTo(v.lte ?? '') }}
              eventTimeZone={event?.timezone}
              defaultMonth={event?.start_date_time ? new Date(event.start_date_time) : undefined}
            />
          </div>

          {sortedItems.length > 0 ? (
          <div className={cn(
            'bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden',
            reportFiltering && 'opacity-60',
          )}>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-800">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('item_name')}
                    >
                      <span className="inline-flex items-center">
                        Item Name
                        <SortIcon field="item_name" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('order_count')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Orders
                        <SortIcon field="order_count" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('quantity_sold')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Sold
                        <SortIcon field="quantity_sold" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('token_revenue')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Token Sales
                        <SortIcon field="token_revenue" />
                      </span>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-200 select-none"
                      onClick={() => handleSort('cash_revenue')}
                    >
                      <span className="inline-flex items-center justify-end">
                        Cash Sales
                        <SortIcon field="cash_revenue" />
                      </span>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Rating
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-[#131313] divide-y divide-neutral-200 dark:divide-neutral-800">
                  {sortedItems.map((item) => (
                    <tr key={item.item_id} className="hover:bg-neutral-50 dark:hover:bg-neutral-900">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                          {item.item_name}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {item.item_price} tokens
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right">
                        {item.order_count.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right font-medium">
                        {item.quantity_sold.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right">
                        {item.token_revenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-100 text-right font-medium">
                        ${item.cash_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <ItemRatingDisplay ratings={itemRatingsMap.get(item.item_id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals row */}
                <tfoot className="bg-neutral-50 dark:bg-neutral-900">
                  <tr className="font-semibold">
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100">
                      Total ({sortedItems.length} items)
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.order_count, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.quantity_sold, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      {sortedItems.reduce((sum, item) => sum + item.token_revenue, 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-sm text-neutral-900 dark:text-neutral-100 text-right">
                      ${sortedItems.reduce((sum, item) => sum + item.cash_revenue, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          ) : (
            <div className="bg-white dark:bg-[#131313] border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 text-center">
              <Package className="h-12 w-12 text-neutral-400 dark:text-neutral-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No Item Data</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {reportDateParams
                  ? 'No item sales in the selected date range.'
                  : 'No item sales data available for this location yet.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Edit Menu Item Modal */}
      {editingItemId && event && (
        <EditMenuItemModal
          itemId={editingItemId}
          event={event}
          tokenValue={tokenValue}
          eventType={eventType}
          onClose={() => setEditingItemId(null)}
        />
      )}

      {/* Create Menu Item Modal */}
      {showCreateModal && location && (
        <CreateMenuItemModal
          eventId={eventIdNum}
          tokenValue={tokenValue}
          eventType={eventType}
          defaultVendorEvent={location}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Location Modal */}
      {showEditLocationModal && location && (
        <EditLocationModal
          location={location}
          eventId={eventIdNum}
          onClose={() => setShowEditLocationModal(false)}
        />
      )}

      {/* Export Login PDF Modal (auto-exports for this location) */}
      {canExportVendorLogins && showExportLoginPdfModal && (
        <ExportLoginPdfModal
          eventId={eventIdNum}
          autoExportVendorEventIds={[locationIdNum]}
          onClose={() => setShowExportLoginPdfModal(false)}
        />
      )}
    </div>
  )
}

// Edit Location Modal Component
interface EditLocationModalProps {
  location: VendorEvent
  eventId: number
  onClose: () => void
}

type EditLocationTab = 'details' | 'users'

export function EditLocationModal({ location, eventId, onClose }: EditLocationModalProps) {
  const updateVendorEvent = useUpdateVendorEvent()
  const deleteVendorEvent = useDeleteVendorEvent()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // Set when the server says this location is gone (deleted in another tab, or
  // opened from a list that was still serving it from cache). Blocks further
  // save attempts against a dead id.
  const [isGone, setIsGone] = useState(false)
  const canDeleteVendors = usePermission('can_delete_vendors')
  const canDeleteVendorEvent = usePermission('delete_vendorevent')
  const canDelete = canDeleteVendors || canDeleteVendorEvent
  const [activeTab, setActiveTab] = useState<EditLocationTab>('details')
  
  const vendorObj = typeof location.vendor === 'object' ? location.vendor as Vendor : null
  const vendorName = vendorObj?.name || 'Event Location'
  
  const locationWithBranding = location as VendorEvent & { logo?: string; background?: string }
  const [formData, setFormData] = useState({
    booth: location.booth || location.name || '',
    description: location.description || '',
    is_approved: location.is_approved ?? true,
    is_hidden: location.is_hidden ?? false,
    categories: (location.categories ?? []) as number[],
  })
  const { data: eventCategoriesData } = useEventCategories(eventId)
  const eventCategories = eventCategoriesData?.results ?? []
  const [error, setError] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [backgroundFile, setBackgroundFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(locationWithBranding.logo || null)
  const [backgroundPreview, setBackgroundPreview] = useState<string | null>(locationWithBranding.background || null)
  const [removeLogo, setRemoveLogo] = useState(false)
  const [removeBackground, setRemoveBackground] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const backgroundInputRef = useRef<HTMLInputElement>(null)

  // Reset form only when editing a different location — keying on the object
  // itself would wipe in-progress edits whenever a parent re-render or query
  // refetch produces a new reference for the same location
  useEffect(() => {
    setFormData({
      booth: location.booth || location.name || '',
      description: location.description || '',
      is_approved: location.is_approved ?? true,
      is_hidden: location.is_hidden ?? false,
      categories: (location.categories ?? []) as number[],
    })
    const branded = location as VendorEvent & { logo?: string; background?: string }
    setLogoPreview(branded.logo || null)
    setBackgroundPreview(branded.background || null)
    setLogoFile(null)
    setBackgroundFile(null)
    setRemoveLogo(false)
    setRemoveBackground(false)
  }, [location.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be 2MB or smaller')
      return
    }
    setError(null)
    setLogoFile(file)
    setRemoveLogo(false)
    const reader = new FileReader()
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setError('Background must be 5MB or smaller')
      return
    }
    setError(null)
    setBackgroundFile(file)
    setRemoveBackground(false)
    const reader = new FileReader()
    reader.onload = (ev) => setBackgroundPreview(ev.target?.result as string)
    reader.readAsDataURL(file)
  }

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation()
    setLogoFile(null)
    setLogoPreview(null)
    setRemoveLogo(true)
    if (logoInputRef.current) logoInputRef.current.value = ''
  }

  const handleRemoveBackground = (e: React.MouseEvent) => {
    e.stopPropagation()
    setBackgroundFile(null)
    setBackgroundPreview(null)
    setRemoveBackground(true)
    if (backgroundInputRef.current) backgroundInputRef.current.value = ''
  }

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    document.body.classList.add('overflow-hidden')
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.classList.remove('overflow-hidden')
    }
  }, [onClose])

  const handleSubmitDetails = async () => {
    setError(null)

    if (!formData.booth.trim()) {
      setError('Location name is required')
      return
    }

    try {
      await updateVendorEvent.mutateAsync({
        vendorEventId: location.id,
        data: {
          booth: formData.booth.trim(),
          description: formData.description.trim(),
          is_approved: formData.is_approved,
          is_hidden: formData.is_hidden,
          categories: formData.categories,
          logo: logoFile ? logoFile : removeLogo ? null : undefined,
          background: backgroundFile ? backgroundFile : removeBackground ? null : undefined,
        },
        eventId,
      })
      onClose()
    } catch (err: unknown) {
      if (isNotFoundError(err)) {
        handleLocationGone()
        return
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location'
      setError(errorMessage)
    }
  }

  // The location was deleted out from under this form. Drop it from every cache
  // that renders locations so the list behind the modal loses the row, and stop
  // the form from PATCHing a dead id again.
  const handleLocationGone = () => {
    setIsGone(true)
    setError('This location no longer exists — it was deleted. Close this window; the list has been refreshed.')
    purgeLocationFromCaches()
  }

  const purgeLocationFromCaches = () => {
    queryClient.removeQueries({ queryKey: ['vendorEvent', location.id] })
    queryClient.removeQueries({ queryKey: ['locationStats', location.id] })
    queryClient.removeQueries({ queryKey: ['vendorEventAdministrators', location.id] })
    invalidateLocationCaches(queryClient, { eventId })
  }

  const handleDelete = async () => {
    const locationName = location.booth || location.name || 'this location'
    if (!confirm(`Are you sure you want to delete "${locationName}"? This will remove the location and all associated data. This action cannot be undone.`)) return
    try {
      await deleteVendorEvent.mutateAsync({ vendorEventId: location.id, eventId })
      onClose()
      navigate({ to: '/events/$eventId/vendors', params: { eventId: String(eventId) } })
    } catch (err: unknown) {
      if (isNotFoundError(err)) {
        // Already deleted — the intent succeeded, so finish the same way.
        purgeLocationFromCaches()
        onClose()
        navigate({ to: '/events/$eventId/vendors', params: { eventId: String(eventId) } })
        return
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete location'
      setError(errorMessage)
    }
  }

  const tabs: { id: EditLocationTab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'details',
      label: 'Details',
      icon: <Info className="h-4 w-4 mr-2" />,
    },
    {
      id: 'users',
      label: 'Users & Access',
      icon: <Users className="h-4 w-4 mr-2" />,
    },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div 
        className="relative bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col sm:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Sidebar - Tab Navigation (hidden on mobile) */}
        <div className="hidden sm:block w-64 bg-neutral-50 dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800 shrink-0">
          {/* Header */}
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              {location.booth || location.name}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{vendorName}</p>
          </div>
          
          {/* Tab Navigation */}
          <nav className="p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  activeTab === tab.id
                    ? 'bg-white dark:bg-neutral-900 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-white/50 dark:hover:bg-neutral-900/50'
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Right Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Mobile Header */}
          <div className="sm:hidden flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 truncate flex items-center">
                <MapPin className="h-5 w-5 mr-2 flex-shrink-0" />
                {location.booth || location.name}
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{vendorName}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors ml-2 flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Mobile Tab Navigation */}
          <div className="sm:hidden flex border-b border-neutral-200 dark:border-neutral-800">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex-1 flex items-center justify-center px-3 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400'
                )}
              >
                {tab.icon}
                <span className="hidden xs:inline">{tab.label}</span>
              </button>
            ))}
          </div>
          
          {/* Desktop Close Button */}
          <div className="hidden sm:block absolute top-4 right-4 z-10">
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                  </div>
                )}

                {/* Location Details Section */}
                <div className="space-y-4">
                  {/* Location Name */}
                  <div>
                    <label htmlFor="booth" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Location Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="booth"
                      value={formData.booth}
                      onChange={(e) => setFormData({ ...formData, booth: e.target.value })}
                      placeholder="Enter location name..."
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter location description..."
                      rows={3}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Toggles stacked in one column so the categories chips get
                      a column of their own rather than a full-width strip. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="space-y-4">
                      {/* Approved */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          Approved
                        </label>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, is_approved: !formData.is_approved })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.is_approved ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.is_approved ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      {/* Hide Location — VendorEvent.is_hidden */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          Hide Location
                        </label>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, is_hidden: !formData.is_hidden })}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.is_hidden ? 'bg-blue-600' : 'bg-neutral-300 dark:bg-neutral-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              formData.is_hidden ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Vendor categories (event-scoped, multi-select) */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Categories
                      </label>
                      {eventCategories.length === 0 ? (
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          No vendor categories exist for this event yet — create them under Locations → Categories.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {eventCategories.map((cat) => {
                            const selected = formData.categories.includes(cat.id)
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    categories: selected
                                      ? formData.categories.filter((id) => id !== cat.id)
                                      : [...formData.categories, cat.id],
                                  })
                                }
                                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                  selected
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:border-neutral-400 dark:hover:border-neutral-500'
                                }`}
                              >
                                {cat.name}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Logo Upload */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Upload Logo
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-900"
                        >
                          <div className="flex flex-col items-center">
                            {logoPreview ? (
                              <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain mb-2" />
                            ) : (
                              <Upload className="w-6 h-6 text-neutral-400 mb-2" />
                            )}
                            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </span>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">PNG, JPG, SVG, WebP up to 2MB</span>
                          </div>
                        </button>
                        {logoPreview && (
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            aria-label="Remove logo"
                            className="absolute top-2 right-2 p-1 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>

                    {/* Background Upload */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Upload Background
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => backgroundInputRef.current?.click()}
                          className="w-full px-4 py-6 border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-center hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors bg-neutral-50 dark:bg-neutral-900"
                        >
                          <div className="flex flex-col items-center">
                            {backgroundPreview ? (
                              <img src={backgroundPreview} alt="Background preview" className="w-16 h-10 object-cover rounded mb-2" />
                            ) : (
                              <Upload className="w-6 h-6 text-neutral-400 mb-2" />
                            )}
                            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                              {backgroundPreview ? 'Change Background' : 'Upload Background'}
                            </span>
                            <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">JPG, PNG, WebP up to 5MB</span>
                          </div>
                        </button>
                        {backgroundPreview && (
                          <button
                            type="button"
                            onClick={handleRemoveBackground}
                            aria-label="Remove background"
                            className="absolute top-2 right-2 p-1 rounded-full bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-800 shadow-sm transition-colors"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <input
                        ref={backgroundInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleBackgroundUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="pt-6 border-t border-neutral-200 dark:border-neutral-800 flex flex-col-reverse sm:flex-row sm:items-center gap-2 sm:gap-3">
                  {canDelete && (
                    <Button
                      variant="outline"
                      onClick={handleDelete}
                      disabled={deleteVendorEvent.isPending || isGone}
                      className="w-full sm:w-auto text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 sm:mr-auto"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {deleteVendorEvent.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                  )}
                  <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto sm:ml-auto">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitDetails}
                    disabled={updateVendorEvent.isPending || isGone}
                    className="w-full sm:w-auto"
                  >
                    {updateVendorEvent.isPending ? 'Updating...' : 'Update Location'}
                  </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Users & Access Tab */}
            {activeTab === 'users' && (
              <LocationUsersTab locationId={location.id} eventId={eventId} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Location Users Tab Component
function LocationUsersTab({ locationId, eventId }: { locationId: number; eventId: number }) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [staffRows, setStaffRows] = useState<{ email: string; groupId: string }[]>([{ email: '', groupId: '' }])
  const [error, setError] = useState<string | null>(null)
  const [editingAdminId, setEditingAdminId] = useState<number | null>(null)
  const [editingGroups, setEditingGroups] = useState<number[]>([])
  
  // Permission checks
  const canAddVendorEventAdmin = usePermission('can_add_vendor_event_admin')
  const canChangeVendorEventAdmin = usePermission('can_change_vendor_event_admin')
  const canDeleteVendorEventAdmin = usePermission('can_delete_vendor_event_admin')
  
  // Queries and mutations
  const { data: adminsData, isLoading: adminsLoading } = useVendorEventAdministrators(locationId, eventId)
  const { data: groupsData } = usePermissionGroups()
  const createAdmin = useCreateVendorEventAdministrator()
  const updateAdmin = useUpdateVendorEventAdministrator()
  const deleteAdmin = useDeleteVendorEventAdministrator()
  
  const administrators = adminsData?.results || []
  const groups = groupsData?.results || []
  
  // Filter to only vendor groups
  const vendorGroups = groups.filter((g: PermissionGroup) => g.name.startsWith('vendor_'))
  
  // Format group name: "vendor_admin" -> "Vendor Admin"
  const formatGroupName = (name: string) => {
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  
  const addRow = () => {
    setStaffRows([...staffRows, { email: '', groupId: '' }])
  }
  
  const removeRow = (index: number) => {
    if (staffRows.length > 1) {
      setStaffRows(staffRows.filter((_, i) => i !== index))
    }
  }
  
  const resetForm = () => {
    setStaffRows([{ email: '', groupId: '' }])
    setShowAddForm(false)
    setError(null)
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    // Validate all rows
    const validRows = staffRows.filter(row => row.email.trim() && row.groupId)
    if (validRows.length === 0) {
      setError('Please enter at least one email and select a role')
      return
    }
    
    try {
      // Create each admin
      for (const row of validRows) {
        await createAdmin.mutateAsync({
          email: row.email.trim(),
          vendor_event: locationId,
          groups: [Number(row.groupId)],
        })
      }
      resetForm()
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add staff member'
      setError(errorMessage)
    }
  }
  
  const handleUpdateAdmin = async (adminId: number) => {
    try {
      await updateAdmin.mutateAsync({
        adminId,
        data: { groups: editingGroups },
        vendorEventId: locationId,
      })
      setEditingAdminId(null)
      setEditingGroups([])
    } catch (err: unknown) {
      console.error('Failed to update admin:', err)
    }
  }
  
  const handleDeleteAdmin = async (adminId: number) => {
    if (!confirm('Are you sure you want to remove this staff member\'s access?')) return
    
    try {
      await deleteAdmin.mutateAsync({
        adminId,
        vendorEventId: locationId,
      })
    } catch (err: unknown) {
      console.error('Failed to delete admin:', err)
    }
  }
  
  const startEditing = (admin: VendorEventAdministrator) => {
    setEditingAdminId(admin.id)
    setEditingGroups(admin.groups?.map(g => g.id) || [])
  }
  
  const getInitials = (admin: VendorEventAdministrator) => {
    if (admin.user?.first_name) {
      return `${admin.user.first_name.charAt(0)}${admin.user.last_name?.charAt(0) || ''}`.toUpperCase()
    }
    return admin.user?.email?.charAt(0).toUpperCase() || '?'
  }
  
  const getDisplayName = (admin: VendorEventAdministrator) => {
    if (admin.user?.first_name || admin.user?.last_name) {
      return `${admin.user.first_name || ''} ${admin.user.last_name || ''}`.trim()
    }
    return admin.user?.email || 'Unknown'
  }
  
  if (adminsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }
  
  // Add Staff Form View
  if (showAddForm) {
    return (
      <div className="space-y-6">
        <div>
          <button
            type="button"
            onClick={resetForm}
            className="inline-flex items-center text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Staff List
          </button>
        </div>
        
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Add Staff Members</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Add one or more staff members to this location</p>
        
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 mb-6">
            {staffRows.map((row, index) => (
              <div key={index} className="flex items-end gap-3 w-full border border-neutral-200 dark:border-neutral-700 rounded-lg p-3 bg-white dark:bg-neutral-900">
                {/* Email */}
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={row.email}
                    onChange={(e) => {
                      const newRows = [...staffRows]
                      newRows[index].email = e.target.value
                      setStaffRows(newRows)
                    }}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                
                {/* Role */}
                <div className="w-48">
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={row.groupId}
                    onChange={(e) => {
                      const newRows = [...staffRows]
                      newRows[index].groupId = e.target.value
                      setStaffRows(newRows)
                    }}
                    required
                    className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-neutral-900 dark:text-neutral-100 bg-white dark:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a role...</option>
                    {vendorGroups.map((group: PermissionGroup) => (
                      <option key={group.id} value={group.id}>
                        {formatGroupName(group.name)}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Remove Button */}
                {staffRows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="p-2 text-neutral-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            
            {/* Add Another Row */}
            <button
              type="button"
              onClick={addRow}
              className="w-full px-4 py-2 border-2 border-dashed border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 rounded-lg hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center text-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Staff Member
            </button>
          </div>
          
          {/* Form Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-neutral-200 dark:border-neutral-800">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {staffRows.length} staff member{staffRows.length !== 1 ? 's' : ''} to add
            </p>
            <div className="flex space-x-3">
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={createAdmin.isPending}>
                <UserPlus className="h-4 w-4 mr-2" />
                {createAdmin.isPending ? 'Adding...' : 'Add Staff'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    )
  }
  
  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Assigned Staff</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Manage staff members who can access and manage <strong>this location</strong>
          </p>
        </div>
        {canAddVendorEventAdmin && (
          <Button onClick={() => setShowAddForm(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Staff
          </Button>
        )}
      </div>
      
      {/* Users List */}
      {administrators.length > 0 ? (
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
          {administrators.map((admin: VendorEventAdministrator) => (
            <div key={admin.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
              <div className="flex items-center justify-between">
                {/* User Info */}
                <div className="flex items-center flex-1 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold shrink-0">
                    {getInitials(admin)}
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {getDisplayName(admin)}
                    </p>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                      {admin.user?.email}
                    </p>
                  </div>
                </div>
                
                {/* Groups/Roles and Actions */}
                <div className="ml-4 flex items-center gap-2 shrink-0">
                  {editingAdminId === admin.id ? (
                    // Edit mode
                    <div className="flex items-center gap-2">
                      <select
                        value={editingGroups[0] || ''}
                        onChange={(e) => setEditingGroups([Number(e.target.value)])}
                        className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                      >
                        {vendorGroups.map((group: PermissionGroup) => (
                          <option key={group.id} value={group.id}>
                            {formatGroupName(group.name)}
                          </option>
                        ))}
                      </select>
                      <Button size="sm" onClick={() => handleUpdateAdmin(admin.id)} disabled={updateAdmin.isPending}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingAdminId(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    // View mode
                    <>
                      {admin.groups && admin.groups.length > 0 ? (
                        admin.groups.map((group: PermissionGroup) => (
                          <span
                            key={group.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {formatGroupName(group.name)}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 italic">No groups assigned</span>
                      )}
                      
                      {/* Actions Dropdown */}
                      {(canChangeVendorEventAdmin || canDeleteVendorEventAdmin) && (
                        <div className="relative group">
                          <button
                            className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                            title="More actions"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          
                          {/* Dropdown */}
                          <div className="absolute right-0 top-full w-48 pt-1 z-10 hidden group-hover:block">
                            <div className="rounded-lg shadow-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 py-1">
                              {canChangeVendorEventAdmin && (
                                <button
                                  onClick={() => startEditing(admin)}
                                  className="w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center"
                                >
                                  <Pencil className="h-4 w-4 mr-3" />
                                  Edit Access
                                </button>
                              )}
                              {canDeleteVendorEventAdmin && (
                                <button
                                  onClick={() => handleDeleteAdmin(admin.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                                >
                                  <Trash2 className="h-4 w-4 mr-3" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Empty State
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neutral-100 dark:bg-neutral-800 mb-4">
              <Users className="h-8 w-8 text-neutral-400 dark:text-neutral-600" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">No staff assigned</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
              Add staff members to grant them access to manage <strong>this location</strong>
            </p>
            {canAddVendorEventAdmin && (
              <Button onClick={() => setShowAddForm(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Your First Staff Member
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Displays aggregated rating for an item
function ItemRatingDisplay({ ratings }: { ratings?: { totalRatings: number; scaleSum: number; scaleCount: number; thumbsUp: number; thumbsDown: number } }) {
  if (!ratings || ratings.totalRatings === 0) {
    return <span className="text-neutral-400 dark:text-neutral-600">—</span>
  }

  if (ratings.scaleCount > 0) {
    const avg = ratings.scaleSum / ratings.scaleCount
    return (
      <div className="flex items-center justify-center gap-1">
        <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{avg.toFixed(1)}</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({ratings.totalRatings})</span>
      </div>
    )
  }

  if (ratings.thumbsUp + ratings.thumbsDown > 0) {
    const pct = (ratings.thumbsUp / (ratings.thumbsUp + ratings.thumbsDown)) * 100
    return (
      <div className="flex items-center justify-center gap-1">
        <ThumbsUp className="h-3.5 w-3.5 text-green-500 fill-green-500" />
        <span className="text-neutral-700 dark:text-neutral-300 font-medium">{pct.toFixed(0)}%</span>
        <span className="text-neutral-400 dark:text-neutral-500 text-xs">({ratings.totalRatings})</span>
      </div>
    )
  }

  return <span className="text-neutral-400 dark:text-neutral-600">—</span>
}

